import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Wrench } from "lucide-react";

/**
 * Placeholder pane used by CMS routes that either (a) haven't been built
 * yet or (b) forward to a scattered legacy admin page. Every CMS section
 * has a stub so the sidebar never lands on a 404.
 *
 * Props:
 *   title        – Section title
 *   description  – One-liner
 *   forwardTo    – Optional string href to a legacy admin page
 *   forwardLabel – Label for the forward CTA
 *   children     – Optional extra content
 */
export default function CmsStub({ title, description, forwardTo, forwardLabel, children }) {
  return (
    <div className="max-w-3xl" data-testid={`cms-stub-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <Link to="/admin/cms" className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to CMS home
      </Link>
      <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
      {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}

      {forwardTo && (
        <Link
          to={forwardTo}
          data-testid="cms-stub-forward"
          className="mt-6 inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
        >
          <ExternalLink className="h-4 w-4" /> {forwardLabel || "Open existing admin surface"}
        </Link>
      )}

      {!forwardTo && !children && (
        <div className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          <Wrench className="mx-auto mb-2 h-5 w-5 text-slate-400" />
          This section is scaffolded — the full editor will ship in the next pass.
        </div>
      )}

      {children && <div className="mt-6">{children}</div>}
    </div>
  );
}
